import React, { useMemo, useContext, useEffect, useRef, lazy, Suspense } from 'react';

import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import qs from 'qs';
import { Routes, Route, useLocation } from 'react-router-dom';

import { Loader } from '../../components/Loader';
import { CustomPageLayout } from '../../containers/CustomPageLayout';
import { DiscoverContext } from '../../context/DiscoverContext';
import { GET_HAS_NEWS } from '../../graphql/local';
import useSearchClient from '../../hooks/useSearchClient';
import { INSIGHTS_SERP_ROUTE, SERP_ROUTES } from '../../routes';
import { LearnerInfo, Tenant } from '../../types';
import { GRID_VIEW, LIST_VIEW } from './const';

const CategoryPage = lazy(() => import('./pages/CategoryPage/CategoryPage'));
const ExpertPage = lazy(() => import('./pages/ExpertPage/ExpertPage'));
const ExpertsPage = lazy(() => import('./pages/ExpertsPage/ExpertsPage'));
const LandingPage = lazy(() => import('./pages/LandingPage/LandingPage'));
const SearchPage = lazy(() => import('./pages/SearchPage/SearchPage'));
const TermPage = lazy(() => import('./pages/TermPage/TermPage'));

interface DiscoverPageProps {
  tenant: Tenant;
  loadingTenant: boolean;
  userInfo?: LearnerInfo;
  userLoading: boolean;
}

export type GetHasNewsResponse = {
  hasNews?: boolean;
};

const DiscoverPage: React.FC<DiscoverPageProps> = ({
  tenant,
  loadingTenant,
  userInfo,
  userLoading,
}) => {
  const { indexName, loadingApiKey, searchClient } = useSearchClient();
  const { data: dataNews } = useQuery<GetHasNewsResponse>(GET_HAS_NEWS);
  const { setDiscoverView } = useContext(DiscoverContext);
  const { search } = useLocation();
  const searchRef = useRef(search);

  const routes = useMemo(
    () => (dataNews?.hasNews ? [...SERP_ROUTES, INSIGHTS_SERP_ROUTE] : SERP_ROUTES),
    [dataNews],
  );

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    let params;
    if (searchRef.current) {
      params = qs.parse(searchRef.current?.slice(1));
    }
    if (params?.grid || params?.grid === '') {
      setDiscoverView(GRID_VIEW);
    } else if (params?.list || params?.list === '') {
      setDiscoverView(LIST_VIEW);
    } else if (userInfo) {
      setDiscoverView(LIST_VIEW);
    }
  }, [userInfo, setDiscoverView]);

  if (loadingTenant) {
    return <Loader />;
  }

  return (
    <>
      <Box>
        <Routes>
          <Route
            path={`/glossary/:term`}
            element={
              <Suspense fallback={<Loader />}>
                <TermPage
                  userInfo={userInfo}
                  tenant={tenant?.title || ''}
                  algoliaIndexName={indexName}
                  searchClient={searchClient}
                  tenantName={tenant?.name}
                />
              </Suspense>
            }
          />
          {routes.map((route) => (
            <Route
              key={route}
              path={route}
              element={
                <Suspense fallback={<Loader />}>
                  <SearchPage
                    algoliaIndexName={indexName}
                    searchClient={searchClient}
                    tenant={tenant}
                    loadingKey={loadingApiKey}
                    tenantName={tenant?.name}
                    userLoading={userLoading}
                  />
                </Suspense>
              }
            />
          ))}
          <Route
            path={`/experts/:expertSlug`}
            element={
              <Suspense fallback={<Loader />}>
                <ExpertPage
                  algoliaIndexName={indexName}
                  searchClient={searchClient}
                  tenant={tenant}
                  loadingKey={loadingApiKey}
                  userLoading={userLoading}
                />
              </Suspense>
            }
          />
          <Route
            path={`/experts`}
            element={
              <Suspense fallback={<Loader />}>
                <ExpertsPage
                  searchClient={searchClient}
                  tenant={tenant}
                  loadingKey={loadingApiKey}
                />
              </Suspense>
            }
          />
          <Route
            path="/:category/:subcategory?"
            element={
              <Suspense fallback={<Loader />}>
                <CategoryPage
                  algoliaIndexName={indexName}
                  searchClient={searchClient}
                  tenant={tenant}
                  loadingKey={loadingApiKey}
                  userLoading={userLoading}
                />
              </Suspense>
            }
          />
          {!!userInfo && (
            <Route
              path="/"
              element={
                <Suspense fallback={<Loader />}>
                  <LandingPage tenant={tenant} />
                </Suspense>
              }
            />
          )}
          <Route path="/" element={<CustomPageLayout userInfo={userInfo} tenant={tenant} />} />
        </Routes>
      </Box>
    </>
  );
};

export default DiscoverPage;
